import { AnalyticsApi, AnalyticsEvent } from '@backstage/core-plugin-api';
import { captureEvent } from './statistics';

/**
 * Adapts Backstage's AnalyticsApi to our usage statistics (statistics.ts).
 *
 * The Backstage-side names — AnalyticsApi, useAnalytics, analyticsApiRef — belong
 * to @backstage/core-plugin-api and cannot be renamed. Everything on our side of
 * the boundary says "statistics", to keep it clearly distinct from the Analytics
 * page at /analytics, which is a customer-facing product feature about engine
 * performance and has nothing to do with this.
 *
 * Plugins capture events through `useAnalytics()` and never import posthog-js
 * themselves. That keeps consent gating in one place (statistics.ts decides
 * whether anything is sent at all), keeps plugin packages free of a vendor
 * dependency, and means swapping providers is a change to this file rather than
 * to every call site.
 *
 * Backstage models an event as an action taken on a subject — `captureEvent('view',
 * 'Regulatory')`. PostHog instead wants one named event with properties, so the two
 * are combined into `<plugin>_<action>` with the subject as a property. The plugin
 * comes from Backstage's analytics context, which it populates automatically, so
 * events are attributable to a product area without every call site repeating it.
 */
export class PostHogStatisticsApi implements AnalyticsApi {
  static create() {
    return new PostHogStatisticsApi();
  }

  captureEvent(event: AnalyticsEvent) {
    const { action, subject, value, attributes, context } = event;
    const plugin = typeof context.pluginId === 'string' ? context.pluginId : 'app';

    captureEvent(`${plugin}_${action}`, {
      subject,
      ...(value !== undefined && { value }),
      // routeRef and extension are useful for telling two call sites apart; the
      // rest of the context is Backstage bookkeeping that would only add noise.
      ...(typeof context.routeRef === 'string' && { route_ref: context.routeRef }),
      ...attributes,
    });
  }
}
